import { motion } from 'framer-motion'
import { LOOP } from '../tokens/motion'
import { cn } from '../lib/cn'

/**
 * A state told by shape and a label. The shape says what kind of state it
 * is; a `hue`, when given, replaces the shape with a small lit dot in that
 * colour — the way the clock shows Logged In — so the job ledger's statuses
 * read apart at a glance while their words stay in the text colour.
 *
 *   idle       hollow ring, faint       nothing is happening
 *   waiting    hollow ring              queued, scheduled
 *   active     solid, breathing         work is running now
 *   review     ring with a centre dot   a person should look
 *   done       solid, quiet             finished
 *   attention  solid, danger            failed, needs a person
 *   clocked-in solid, green             ready for work
 *   clocked-out solid, red              not taking new work
 */
export type StatusTone = 'idle' | 'waiting' | 'active' | 'review' | 'done' | 'attention' | 'clocked-in' | 'clocked-out'

/** The colours a state can wear. */
export type StatusHue = 'blue' | 'amber' | 'green' | 'violet' | 'red'

const HUE_DOT: Record<StatusHue, string> = {
  blue: 'bg-status-blue',
  amber: 'bg-status-amber',
  green: 'bg-status-green',
  violet: 'bg-status-violet',
  red: 'bg-status-red',
}

export function StatusMark({ tone, hue, className }: { tone: StatusTone; hue?: StatusHue; className?: string }) {
  const base = cn('relative inline-block size-2 shrink-0 rounded-full', className)
  if (hue) return <span aria-hidden className={cn(base, HUE_DOT[hue])} />

  if (tone === 'active') {
    return (
      <motion.span
        aria-hidden
        className={cn(base, 'bg-foreground')}
        animate={{ opacity: [0.35, 1, 0.35] }}
        transition={LOOP.pulse}
      />
    )
  }
  if (tone === 'review') {
    return (
      <span aria-hidden className={cn(base, 'border-[1.5px] border-foreground/70')}>
        <span className="absolute inset-[2px] rounded-full bg-foreground/70" />
      </span>
    )
  }
  return (
    <span
      aria-hidden
      className={cn(
        base,
        tone === 'idle' && 'border border-muted-foreground/40',
        tone === 'waiting' && 'border-[1.5px] border-muted-foreground',
        tone === 'done' && 'bg-foreground/45',
        tone === 'attention' && 'bg-danger',
        tone === 'clocked-in' && 'bg-positive',
        tone === 'clocked-out' && 'bg-danger',
      )}
    />
  )
}

/**
 * A mark and its label, the way a state is always shown. Colour lives in the
 * dot; the words stay in the text colour (muted when nothing is happening),
 * and only a failure that needs a person turns its words red.
 */
export function StatusLabel({ tone, hue, label, className }: { tone: StatusTone; hue?: StatusHue; label: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-caption',
        tone === 'attention' ? 'text-danger' : tone === 'idle' && !hue ? 'text-muted-foreground' : 'text-foreground',
        className,
      )}
    >
      <StatusMark tone={tone} hue={hue} />
      <span className={cn(tone === 'active' && 'wf-shimmer')}>{label}</span>
    </span>
  )
}
